import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { getRandomColor, getMaxOrder, areTagsEqual } from "../../../utils/utils";
import axios from 'axios';
import { GET_TAGS, DELETE_TAG } from "../../../constants/endpoints";

export function useCustomDropdown({ value = [], onChange, setError }) {
  const [optionsList, setOptionsList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [colorMenuPosition, setColorMenuPosition] = useState({ top: 0, left: 0 });
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 260 });
  const [draggedTag, setDraggedTag] = useState(null);

  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const lastOrderRef = useRef(1);

  // Sync selectedTags with external value prop
  useEffect(() => {
    if (!areTagsEqual(value, selectedTags)) {
      setSelectedTags(value || []);
    }
  }, [value]);

  // Notify parent of selectedTags changes
  useEffect(() => {
    onChange?.(selectedTags);
  }, [selectedTags]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
        setOpenActionMenu(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // close dropdown when page is scrolled or resized
   useEffect(() => {
    if (!isOpen) return; 
     const handleResizeOrScroll = () => {
        setIsOpen(false);
        setSearch("");
        setOpenActionMenu(null);
    }; 

    window.addEventListener("resize", handleResizeOrScroll);
    return () => {
      window.removeEventListener("resize", handleResizeOrScroll);
    };
   }, [isOpen]) 

  // Close color menu on scroll or resize
  useEffect(() => {
    if (!openActionMenu) return;
    const handleClose = () => setOpenActionMenu(null);
    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose);
    return () => {
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [openActionMenu]);

  //  Dropdown Position
  const updateDropdownPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom + 8, left: rect.left, width: rect.width });
  };

  const throttledUpdate = useCallback(() => {
    requestAnimationFrame(() => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition((prev) => {
        if (
          prev.top === rect.bottom + 8 &&
          prev.left === rect.left &&
          prev.width === rect.width
        ) return prev;
        return { top: rect.bottom + 8, left: rect.left, width: rect.width };
      });
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", throttledUpdate, true);
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", throttledUpdate, true);
    };
  }, [isOpen]);

  // Fetch Tags debounce on search
  useEffect(() => {
    setError(null);
    let cancelled = false;
    const query = search.replace(/\s+/g, " ").trim();
    const delay = setTimeout(
      () => {
      axios.get(GET_TAGS, {params: { search: query || undefined}})
      .then((response) => {
          if (!cancelled) {
            const sortedData = response.data.response; 
            setOptionsList(sortedData);
            const maxOrder = getMaxOrder(sortedData); 
            lastOrderRef.current = Math.max(lastOrderRef.current, maxOrder);
          }
        })
        .catch((err) => {
          if(!cancelled){
            setError(err?.response?.data?.message || "Failed to fetch tags. Please try again.");
          }
        });
      },
      query ? 500 : 0,
    );
    return () => {
      cancelled = true;
      clearTimeout(delay);
    };
  }, [search]);

  //  Derived
  const selectTitle = useMemo(
    () => new Set(selectedTags.map((tag) => tag.title)),
    [selectedTags],
  );

  const filtered = useMemo(() => {
    const searchText = search.toLowerCase();
    const selectedSet = new Set(selectedTags.map((t) => t.title));
    if (!search.trim()) {
      return [
        ...selectedTags,
        ...optionsList.filter((opt) => !selectedSet.has(opt.title)),
      ];
    }
    return [
      ...selectedTags.filter((opt) => opt.title.toLowerCase().includes(searchText)),
      ...optionsList.filter(
        (opt) =>
          opt.title.toLowerCase().includes(searchText) &&
          !selectedSet.has(opt.title),
      ),
    ];
  }, [selectedTags, optionsList, search]);

  const showCreate =
    search.trim() !== "" &&
    !optionsList.some(
      (o) => o.title.toLowerCase() === search.trim().toLowerCase(),
    );

  //  Color Menu Position
  const updateColorMenuPosition = (buttonElement) => {
    if (!buttonElement) return;
    const rect = buttonElement.getBoundingClientRect();
    const dropdownHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const topPosition =
      spaceBelow < dropdownHeight && spaceAbove > dropdownHeight
        ? rect.top - dropdownHeight
        : rect.top;
    setColorMenuPosition({ top: topPosition, left: rect.right + 8 });
  };

  //  Handlers 
  const handleSelect = (opt) => {
    setSelectedTags((prev) => {
      if (prev.some((tag) => tag.title === opt.title)) return prev;
      return [...prev, opt];
    });
    setSearch("");
    setOpenActionMenu(null);
  };

  const handleCreate = () => {
    setError(null);
    const newOrder = lastOrderRef.current + 1;
    lastOrderRef.current = newOrder; 

    const newOpt = { title: search.trim(), tag_order: newOrder, bg: getRandomColor() };
    setOptionsList((prev) => [...prev, newOpt]);
    setSelectedTags((prev) => [...prev, newOpt]);
    setSearch("");
  };

  const handleColorChange = (option, color) => {
    const updatedOption = { ...option, ...color };
    setOptionsList((prev) =>
      prev.map((opt) => (opt.title === option.title ? updatedOption : opt)),
    );
    setSelectedTags((prev) =>
      prev.map((tag) => (tag.title === option.title ? updatedOption : tag)),
    );
    setOpenActionMenu(null);
  };

  const handleRemoveTag = (tag) => {
    setError(null);
    setSelectedTags((prev) => prev.filter((t) => t.title !== tag.title));
  };

  const handleTagDrop = (targetTag) => { 
   
    if (!draggedTag) {
      setDraggedTag(null);
      return;
    }
      const draggedIndex = selectedTags.findIndex((tag) => tag.title === draggedTag);  
      const targetIndex = selectedTags.findIndex((tag) => tag.title === targetTag.title);

      if (draggedIndex === -1 || targetIndex === -1){
        setDraggedTag(null);
        return;
      };
      const nextTags = [...selectedTags];  

      const tempOrder = nextTags[draggedIndex].tag_order; 
      nextTags[draggedIndex].tag_order = nextTags[targetIndex].tag_order;
      nextTags[targetIndex].tag_order = tempOrder;

      const tempTag = nextTags[draggedIndex];
      nextTags[draggedIndex] = nextTags[targetIndex];
      nextTags[targetIndex] = tempTag;

    setSelectedTags(nextTags);

       setOptionsList((currentOptions) =>
      currentOptions.map((option) => { 
       
        const reorderedTag = nextTags.find((tag) => tag.title === option.title);
        return reorderedTag || option;
      }),
    );
    setDraggedTag(null);
  };

  const handleDelete = (opt) => {
    if (opt.id) {
      axios.delete(DELETE_TAG + `/${opt.id}`)
        .then((res) => {
          if (res.data.status === false) {
            setError(res.data.message);
            return;
          }
          setOptionsList((prev) => prev.filter((o) => o.id !== opt.id));
          setSelectedTags((prev) => prev.filter((tag) => tag.title !== opt.title));
          setOpenActionMenu(null);
        })
        .catch((err) => {
          setError(err?.response?.data?.message || "Failed to delete tag. Please try again.");
        });
    }else{
      setOptionsList((prev) => prev.filter((o) => o.title !== opt.title));
      setSelectedTags((prev) => prev.filter((tag) => tag.title !== opt.title));
      setOpenActionMenu(null);
    }
  };

  const handleToggleOpen = () => {
    updateDropdownPosition();
    setIsOpen((prev) => !prev);
  };

  return {
    dropdownRef,
    triggerRef,
    selectedTags,
    isOpen,
    search,
    setSearch,
    openActionMenu,
    setOpenActionMenu,
    colorMenuPosition,
    dropdownPosition,
    draggedTag,
    setDraggedTag,
    filtered,
    showCreate,
    selectTitle,
    handleSelect,
    handleCreate,
    handleColorChange,
    handleRemoveTag,
    handleTagDrop,
    handleDelete,
    handleToggleOpen,
    updateColorMenuPosition,
  };
}